import React from 'react';
import { cn } from '../../lib/utils';

interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'secondary' | 'outline' | 'ghost';
  size?: 'sm' | 'md' | 'lg' | 'xl';
  loading?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    { className, variant = 'primary', size = 'md', loading = false, children, disabled, ...props },
    ref,
  ) => {
    const baseStyles =
      'inline-flex rounded-md w-full items-center justify-center font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 cursor-pointer gap-2';

    const variants = {
      primary: 'bg-gradient-primary text-white hover:opacity-90 shadow-card rounded-button-primary',
      secondary: 'bg-secondary text-white hover:opacity-90 rounded-form shadow-form-field',
      outline: 'border border-text hover:bg-surface text-text rounded-form shadow-form-field',
      ghost: 'hover:bg-surface text-text hover:text-white rounded-form',
    };

    const sizes = {
      sm: 'h-9 px-3 text-sm',
      md: 'h-11 px-6 text-button-primary',
      lg: 'h-13 px-8 text-button-lg',
      xl: 'h-15 px-10 text-xl',
    };

    return (
      <button
        className={cn(baseStyles, variants[variant], sizes[size], className)}
        ref={ref}
        disabled={disabled || loading}
        {...props}
      >
        {loading ? (
          <>
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
            {children}
          </>
        ) : (
          children
        )}
      </button>
    );
  },
);

Button.displayName = 'Button';

export { Button };
